import { Injectable, Injector, signal, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { OutlookSessionService } from './outlook-session.service';

@Injectable({
  providedIn: 'root',
})
export class InitializationService {
  private readonly translateService = inject(TranslateService);
  private readonly injector = inject(Injector);

  // 15s delay for refresh button - matches retry timeout
  private readonly SHOW_REFRESH_DELAY_WINDOW = 15000;

  private refreshButtonTimer?: number;
  private refreshSectionElement?: HTMLElement;

  initializingApp = signal(false);
  showRefreshButton = signal(false);

  startInitialization(): void {
    this.initializingApp.set(true);
    this.showRefreshButton.set(false);

    this.initializeLoaderElement();

    this.refreshButtonTimer = window.setTimeout(() => {
      if (this.initializingApp()) {
        this.showRefreshButton.set(true);
        this.refreshSectionElement?.classList.add('show');
      }
    }, this.SHOW_REFRESH_DELAY_WINDOW);
  }

  stopInitialization(): void {
    this.initializingApp.set(false);
    this.showRefreshButton.set(false);
    this.hideLoader();

    this.refreshSectionElement = undefined;

    if (this.refreshButtonTimer) {
      clearTimeout(this.refreshButtonTimer);
      this.refreshButtonTimer = undefined;
    }
  }

  private initializeLoaderElement(): void {
    let loader = document.getElementById('initial-app-loader');

    if (!loader) {
      loader = document.createElement('div');
      loader.id = 'initial-app-loader';
      loader.className = 'app-loading';
      loader.innerHTML = `
      <div class="loading-spinner"></div>
      <div id="refresh-section" class="refresh-section">
        <p class="refresh-message">Taking longer than expected?</p>
        <button class="refresh-button">
          Refresh
        </button>
        <button class="logout-button">
          Logout
        </button>
      </div>
    `;
      document.body.appendChild(loader);

      this.refreshSectionElement = loader.querySelector(
        '#refresh-section',
      ) as HTMLElement;
      const refreshButtonElement = loader.querySelector(
        '.refresh-button',
      ) as HTMLButtonElement;
      const refreshMessageElement = loader.querySelector(
        '.refresh-message',
      ) as HTMLElement;
      const logoutButtonElement = loader.querySelector(
        '.logout-button',
      ) as HTMLButtonElement;

      this.applyTranslatedText(refreshButtonElement, 'outlook.buttons.refresh');
      this.applyTranslatedText(
        refreshMessageElement,
        'outlook.shared.labels.initializeMessage',
      );
      this.applyTranslatedText(logoutButtonElement, 'outlook.buttons.logout');

      refreshButtonElement.addEventListener('click', () => {
        window.location.reload();
      });

      logoutButtonElement.addEventListener('click', () => {
        this.injector.get(OutlookSessionService).logout('loader-logout');
      });
    }
  }

  private applyTranslatedText(element: HTMLElement, key: string): void {
    this.translateService.get(key).subscribe((text) => {
      element.textContent = text;
    });
  }

  private hideLoader(): void {
    const loader = document.getElementById('initial-app-loader');
    if (loader) {
      loader.remove();
    }
  }
}
